import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, MapPin, Pencil, Archive, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, inr, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EntityForm from "@/components/EntityForm";
import EntityPage from "@/components/EntityPage";
import { PageHeader, Badge2, Kpi, Empty, Mocked } from "@/components/common";

const IMGS = [
  "https://images.unsplash.com/photo-1760611656071-a8bef0578874?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1783667440357-bf89d00c2077?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

export default function Projects() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const load = () => api.get("/projects").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const list = (rows || []).filter((p) => showArchived || p.status !== "Archived");
  return (
    <div data-testid="projects-page">
      <PageHeader label="Plan" title="Projects" subtitle="Every project's details, pricing, inventory and launch timeline in one place.">
        <Button variant="secondary" onClick={() => setShowArchived((s) => !s)} data-testid="projects-toggle-archived">{showArchived ? "Hide archived" : "Show archived"}</Button>
        {can("projects", "write") && <Button onClick={() => setOpen(true)} data-testid="projects-create-button"><Plus className="h-4 w-4 mr-1" />New Project</Button>}
      </PageHeader>
      {rows && !list.length && <Empty title="No projects yet">Create your first project to unlock AI intelligence.</Empty>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {list.map((p, i) => (
          <button key={p.id} onClick={() => nav(`/projects/${p.id}`)} className="text-left lx-card p-0 overflow-hidden group fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`project-card-${p.id}`}>
            <div className="h-44 overflow-hidden relative">
              <img src={IMGS[i % 3]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-3 left-3"><Badge2>{p.status}</Badge2></div>
            </div>
            <div className="p-5">
              <div className="font-bold text-lg">{p.name}</div>
              <div className="text-sm text-slate-400 flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{p.locality}, {p.city}</div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <div><div className="text-slate-500">Units</div><div className="font-mono font-semibold">{num(p.total_units)}</div></div>
                <div><div className="text-slate-500">₹ / sq ft</div><div className="font-mono font-semibold">{num(p.price_per_sqft)}</div></div>
                <div><div className="text-slate-500">Launch</div><div className="font-mono font-semibold">{p.launch_date || "-"}</div></div>
              </div>
            </div>
          </button>
        ))}
      </div>
      <EntityForm entity="projects" refs={{}} open={open} onClose={() => setOpen(false)} defaults={{ status: "Planning", project_type: "Residential" }} onSaved={(p) => nav(`/projects/${p.id}`)} />
    </div>
  );
}

const REPORT_LINKS = [["project_intelligence", "/intelligence/project"], ["buyer_intelligence", "/intelligence/buyer"], ["community_intelligence", "/intelligence/community"],
  ["corporate_intelligence", "/intelligence/corporate"], ["competitor_intelligence", "/intelligence/competitor"], ["brand_positioning", "/brand/positioning"],
  ["brand_ambassador", "/brand/ambassadors"], ["marketing_plan", "/marketing/planner"], ["launch_orchestration", "/launch"]];

export function ProjectDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [p, setP] = useState(null);
  const [inv, setInv] = useState([]);
  const [reports, setReports] = useState([]);
  const [edit, setEdit] = useState(false);
  const load = () => api.get(`/projects/${id}`).then((r) => setP(r.data));
  useEffect(() => {
    load();
    api.get("/inventory", { params: { project_id: id } }).then((r) => setInv(r.data));
    if (can("intelligence")) api.get("/ai/reports", { params: { project_id: id } }).then((r) => setReports(r.data));
  }, [id]); // eslint-disable-line
  if (!p) return <div className="text-slate-500">Loading...</div>;
  const count = (s) => inv.filter((u) => u.status === s).length;
  const archive = async () => {
    if (!window.confirm("Archive this project?")) return;
    try { await api.put(`/projects/${id}`, { status: "Archived" }); toast.success("Project archived"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const d = 0.01;
  return (
    <div data-testid="project-detail-page">
      <PageHeader label={`${p.project_type || "Project"} · ${p.city}`} title={p.name} subtitle={p.description}>
        <Badge2>{p.status}</Badge2>
        {can("projects", "write") && <Button variant="secondary" onClick={() => setEdit(true)} data-testid="project-edit-button"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
        {can("projects", "write") && p.status !== "Archived" && <Button variant="secondary" onClick={archive} data-testid="project-archive-button"><Archive className="h-4 w-4 mr-1" />Archive</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Total Units" value={num(p.total_units)} testid="project-kpi-units" />
        <Kpi label="Available" value={count("Available")} accent="text-cyan-400" testid="project-kpi-available" />
        <Kpi label="Booked" value={count("Booked")} testid="project-kpi-booked" />
        <Kpi label="Price Range" value={`${inr(p.price_min)} - ${inr(p.price_max)}`} testid="project-kpi-price" />
      </div>
      <Tabs defaultValue="overview">
        <TabsList><TabsTrigger value="overview" data-testid="project-tab-overview">Overview</TabsTrigger><TabsTrigger value="inventory" data-testid="project-tab-inventory">Inventory</TabsTrigger><TabsTrigger value="intel" data-testid="project-tab-intel">AI Intelligence</TabsTrigger></TabsList>
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lx-card space-y-3">
              {[["Locality", p.locality], ["Address", p.address], ["Configurations", p.configurations], ["Price / sq ft", p.price_per_sqft && `₹${num(p.price_per_sqft)}`], ["RERA", p.rera_number]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-800/60 pb-2 text-sm"><span className="text-slate-500">{k}</span><span className="text-right">{v || "-"}</span></div>
              ))}
              <div><div className="text-slate-500 text-sm mb-2">Amenities</div><div className="flex flex-wrap gap-2">{(p.amenities || "").split(",").filter(Boolean).map((a) => <Badge2 key={a} tone="cyan">{a.trim()}</Badge2>)}</div></div>
              <div className="pt-2"><div className="text-slate-500 text-sm mb-3 flex items-center gap-2"><Calendar className="h-4 w-4" />Launch Timeline</div>
                <div className="flex items-center gap-3 text-xs font-mono">
                  <div className="flex-1 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3"><div className="text-slate-500">LAUNCH</div>{p.launch_date || "TBD"}</div>
                  <div className="h-px w-6 bg-slate-700" />
                  <div className="flex-1 rounded-lg border border-slate-700 p-3"><div className="text-slate-500">POSSESSION</div>{p.possession_date || "TBD"}</div>
                </div>
              </div>
            </div>
            <div className="lx-card p-0 overflow-hidden min-h-[320px] relative">
              <div className="absolute top-3 left-3 z-10"><Mocked what="GOOGLE MAPS MOCKED · OSM PREVIEW" /></div>
              {p.lat && p.lng ? (
                <iframe title="map" className="w-full h-full min-h-[320px]" data-testid="project-map"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.lng - d},${p.lat - d},${p.lng + d},${p.lat + d}&layer=mapnik&marker=${p.lat},${p.lng}`} />
              ) : <div className="grid place-items-center h-full text-sm text-slate-500">Add latitude & longitude to view the map</div>}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="inventory" className="mt-6">
          <EntityPage entity="inventory" filters={{ project_id: id }} hideProjectFilter defaults={{ project_id: id, status: "Available" }} />
        </TabsContent>
        <TabsContent value="intel" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {REPORT_LINKS.map(([t, to]) => {
              const r = reports.find((x) => x.type === t);
              return (
                <Link key={t} to={`${to}?project=${id}`} className="lx-card hover:border-cyan-500/40" data-testid={`project-intel-${t}`}>
                  <div className="flex items-center justify-between"><Sparkles className="h-4 w-4 text-cyan-400" />{r ? <Badge2>{r.status}</Badge2> : <Badge2 tone="slate">Not generated</Badge2>}</div>
                  <div className="font-semibold mt-3 capitalize">{t.replace(/_/g, " ")}</div>
                  <div className="text-xs text-slate-500 mt-1">{r ? `Last run ${r.created_at.slice(0, 10)}` : "Generate with AI"}</div>
                </Link>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
      <EntityForm entity="projects" refs={{}} open={edit} initial={p} onClose={() => setEdit(false)} onSaved={load} />
    </div>
  );
}
